import * as React from "react";
import {
  PageHeader,
  Alert,
  Table,
  Button,
  Space,
  InputNumber,
  Modal,
  Descriptions,
  Tag,
  Empty,
  message as antdMessage,
  Radio,
} from "antd";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  InventoryClient,
  MoveItemDto,
  ProductStockEntryDto,
  ProductStockEntriesResponseDto,
  PositionLabelClient,
  PositionStockEntryDto,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";
import { BarcodeScanResult } from "../hooks/useBarcodeScanner";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";
import {
  parseProductBarcode,
  isPositionCode,
  extractPosition,
  formatBarcodeForDisplay,
} from "../utils/barcodeUtils";
import { playAudioFeedback, AudioFeedbackType } from "../utils/audioFeedback";
import { useNotification } from "../hooks/useNotification";
import { useUserPosition } from "../hooks/useUserPosition";
import { CameraScanner } from "./shared/CameraScanner";
import { NumericKeyboard } from "./shared/NumericKeyboard";

// Initialize API clients at module level
const inventoryClient = new InventoryClient(new ApiConfig());
const positionLabelClient = new PositionLabelClient(new ApiConfig());

// LocalStorage key for session persistence
const STORAGE_KEY = "inventory-move-session";

type MoveMode = "permanent" | "temporary";

interface ScannedItem {
  id: string; // unique identifier for React keys
  productCode: string;
  productName?: string;
  batchNumber: string;
  quantity: number;
  unit?: string;
  skuId?: number;
}

// Minimal item data for localStorage
interface StoredItem {
  productCode: string;
  batchNumber: string;
  quantity: number;
  skuId?: number;
}

interface StoredSession {
  scanPhase: ScanPhase;
  fromPosition: string;
  toPosition: string;
  items: StoredItem[];
  moveMode: MoveMode;
  selectedPrinter: number;
}

type ScanPhase =
  | "WAITING_SOURCE"
  | "SCANNING_ITEMS"
  | "WAITING_DESTINATION"
  | "READY_TO_SUBMIT";

const InventoryMove: React.FC = () => {
  // Core scanning state
  const [scanPhase, setScanPhase] = React.useState<ScanPhase>("WAITING_SOURCE");
  const [fromPosition, setFromPosition] = React.useState<string>("");
  const [toPosition, setToPosition] = React.useState<string>("");
  const [scannedItems, setScannedItems] = React.useState<ScannedItem[]>([]);

  // Mode and printer selection
  const [moveMode, setMoveMode] = React.useState<MoveMode>("permanent");
  const [selectedPrinter, setSelectedPrinter] = React.useState<number>(6101);

  // UI state
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCreatingTempPosition, setIsCreatingTempPosition] =
    React.useState(false);
  const [cameraStatus, setCameraStatus] = React.useState<
    "idle" | "initializing" | "ready" | "error"
  >("idle");

  // Product locations - full product data with stock entries
  const [productLocations, setProductLocations] = React.useState<
    Map<number, ProductStockEntriesResponseDto>
  >(new Map());

  // Source position inventory
  const [sourcePositionInventory, setSourcePositionInventory] = React.useState<
    PositionStockEntryDto[]
  >([]);
  const [isLoadingInventory, setIsLoadingInventory] = React.useState(false);

  // Flag to prevent saving during initial load
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Notification system (floating messages, no layout shift)
  const notify = useNotification();

  // User position for quick set
  const {
    userPosition,
    userName,
    isAvailable: isUserPositionAvailable,
  } = useUserPosition();

  // Visual notification only (no audio)
  const showNotification = React.useCallback(
    (msg: string, type: "success" | "error" | "info" | "warning") => {
      notify.notify(type, msg);
    },
    [notify],
  );

  // Combined feedback handler (visual + audio)
  const showFeedbackWithSound = React.useCallback(
    (
      msg: string,
      type: "success" | "error" | "info" | "warning",
      sound: AudioFeedbackType,
    ) => {
      notify.notify(type, msg);
      playAudioFeedback(sound);
    },
    [notify],
  );

  // DEBUG: Visible log for debugging scanner issues
  const [debugLog, setDebugLog] = React.useState<string[]>([]);
  const [showDebugLog, setShowDebugLog] = React.useState(false);
  const addDebugLog = React.useCallback((msg: string) => {
    setDebugLog((prev) => [
      ...prev.slice(-9),
      `${new Date().toLocaleTimeString()}: ${msg}`,
    ]);
  }, []);

  // Save current session to localStorage
  const saveToLocalStorage = React.useCallback(() => {
    try {
      const session: StoredSession = {
        scanPhase,
        fromPosition,
        toPosition,
        items: scannedItems.map((item) => ({
          productCode: item.productCode,
          batchNumber: item.batchNumber,
          quantity: item.quantity,
          skuId: item.skuId,
        })),
        moveMode,
        selectedPrinter,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error("Failed to save session to localStorage:", error);
    }
  }, [
    scanPhase,
    fromPosition,
    toPosition,
    scannedItems,
    moveMode,
    selectedPrinter,
  ]);

  // Clear localStorage
  const clearLocalStorage = React.useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  }, []);

  // Load position inventory
  const loadPositionInventory = React.useCallback(
    async (positionId: string) => {
      setIsLoadingInventory(true);
      try {
        const inventory = await inventoryClient.getPositionProducts(positionId);
        setSourcePositionInventory(inventory);
        return inventory;
      } catch (error) {
        console.error("Failed to load position inventory:", error);
        setSourcePositionInventory([]);
        return null;
      } finally {
        setIsLoadingInventory(false);
      }
    },
    [],
  );

  // Load product details and stock entries from backend
  const loadProductData = React.useCallback(
    async (
      productCode: string,
    ): Promise<ProductStockEntriesResponseDto | null> => {
      try {
        const productData =
          await inventoryClient.getStockEntriesByCode(productCode);

        // Update product locations map with full product data
        setProductLocations((prev) =>
          new Map(prev).set(productData.SkuId, productData),
        );

        return productData;
      } catch (error) {
        console.error("Failed to load product data:", error);
        return null;
      }
    },
    [],
  );

  // Load saved session from localStorage on mount
  React.useEffect(() => {
    const loadFromLocalStorage = async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) {
          setIsInitialLoad(false);
          return;
        }

        const session: StoredSession = JSON.parse(saved);

        // Restore state
        setFromPosition(session.fromPosition);
        setToPosition(session.toPosition);
        setScanPhase(session.scanPhase);
        setMoveMode(session.moveMode || "permanent");
        setSelectedPrinter(session.selectedPrinter || 6101);

        // Load position inventory if we have a source position
        if (session.fromPosition) {
          await loadPositionInventory(session.fromPosition);
        }

        // Restore items with fresh product data from API
        if (session.items.length > 0) {
          const restoredItems: ScannedItem[] = [];

          for (const storedItem of session.items) {
            const productData = await loadProductData(storedItem.productCode);

            restoredItems.push({
              id: `${Date.now()}-${Math.random()}`,
              productCode: storedItem.productCode,
              batchNumber: storedItem.batchNumber,
              quantity: storedItem.quantity,
              skuId: productData?.SkuId ?? storedItem.skuId,
              productName: productData?.ProductName,
              unit: productData?.Unit,
            });
          }

          setScannedItems(restoredItems);
        }
      } catch (error) {
        console.error("Failed to load session from localStorage:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadFromLocalStorage();
  }, [loadProductData, loadPositionInventory]);

  // Auto-save to localStorage when state changes (after initial load)
  React.useEffect(() => {
    if (!isInitialLoad) {
      saveToLocalStorage();
    }
  }, [isInitialLoad, saveToLocalStorage]);

  // Handle scanned code processing
  const processScannedCode = React.useCallback(
    async (code: string) => {
      if (!code) return;

      // DEBUG: Log received code and position check
      const debugMsg = `Raw: "${code}" | lastChar: "${code.slice(-1)}" (${code.charCodeAt(code.length - 1)}) | isPosition: ${isPositionCode(code)}`;
      console.log("[InventoryMove]", debugMsg);
      addDebugLog(debugMsg);

      // Check for position marker (@)
      if (isPositionCode(code)) {
        const position = extractPosition(code);
        if (!position) {
          showFeedbackWithSound("Neplatná pozícia!", "error", "invalidCode");
          return;
        }

        // If waiting for source position
        if (scanPhase === "WAITING_SOURCE") {
          setFromPosition(position);
          setScanPhase("SCANNING_ITEMS");
          showFeedbackWithSound(
            `Zdrojová pozícia: ${position}. Skenujte produkty.`,
            "success",
            "positionScan",
          );
          // Load position inventory
          loadPositionInventory(position);
          return;
        }

        // If scanning items or ready to submit - this becomes destination
        if (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") {
          if (scannedItems.length === 0) {
            showFeedbackWithSound(
              "Najprv naskenujte aspoň jeden produkt!",
              "error",
              "blocked",
            );
            return;
          }

          // In temporary mode, prevent scanning positions - user must use the button
          if (moveMode === "temporary") {
            showFeedbackWithSound(
              "V režime dočasnej pozície použite tlačidlo na vytvorenie pozície!",
              "warning",
              "requireInput",
            );
            return;
          }

          if (position === fromPosition) {
            showFeedbackWithSound(
              "Cieľová pozícia nemôže byť rovnaká ako zdrojová!",
              "error",
              "validation",
            );
            return;
          }

          setToPosition(position);
          setScanPhase("READY_TO_SUBMIT");
          showFeedbackWithSound(
            `Cieľová pozícia: ${position}. Skontrolujte a potvrďte presun.`,
            "success",
            "positionScan",
          );
          return;
        }
      }

      // Parse product barcode
      if (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") {
        const parsed = parseProductBarcode(code);
        if (!parsed) {
          showFeedbackWithSound(
            "Neplatný formát kódu produktu!",
            "error",
            "invalidCode",
          );
          return;
        }

        // Load product details and stock entries in one call
        const productData = await loadProductData(parsed.productCode);

        // Validate stock availability at source position
        if (
          fromPosition &&
          productData &&
          productData.StockEntries &&
          productData.StockEntries.length > 0
        ) {
          const stockAtSource = productData.StockEntries.find(
            (entry) =>
              entry.PositionId === fromPosition &&
              entry.BatchNumber === parsed.batchNumber,
          );

          if (stockAtSource) {
            // Only validate quantity if we know the available amount
            if (stockAtSource.Quantity !== null) {
              // Calculate already scanned quantity from this position/batch
              const alreadyScanned = scannedItems
                .filter(
                  (item) =>
                    item.skuId === productData.SkuId &&
                    item.batchNumber === parsed.batchNumber,
                )
                .reduce((sum, item) => sum + item.quantity, 0);

              const availableQuantity = stockAtSource.Quantity;
              const remainingQuantity = availableQuantity - alreadyScanned;

              if (parsed.quantity > remainingQuantity) {
                showFeedbackWithSound(
                  `Nedostatočné množstvo! Dostupné: ${remainingQuantity} ${productData.Unit}, požadované: ${parsed.quantity} ${productData.Unit}`,
                  "error",
                  "validation",
                );
                return;
              }
            }
          } else {
            // Product has stock entries but not at this position
            // Check if position is controlled (any item has non-null Quantity)
            const isPositionControlled =
              Array.isArray(sourcePositionInventory) &&
              sourcePositionInventory.some((item) => item.Quantity !== null);
            if (isPositionControlled) {
              showFeedbackWithSound(
                `Chyba: Produkt ${parsed.productCode} so šaržou "${parsed.batchNumber}" sa nenachádza na kontrolovanej pozícii ${fromPosition}.`,
                "error",
                "validation",
              );
              return;
            }
            // If not controlled, allow the scan without any warning
          }
        }

        setScannedItems((prev) => {
          // Check if item with same product code and batch number already exists
          const existingIndex = prev.findIndex(
            (item) =>
              item.productCode === parsed.productCode &&
              item.batchNumber === parsed.batchNumber,
          );

          if (existingIndex >= 0) {
            // Update existing item's quantity
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              quantity: updated[existingIndex].quantity + parsed.quantity,
            };
            showFeedbackWithSound(
              `Zvýšené množstvo: ${formatBarcodeForDisplay(parsed, productData?.Unit)} (celkom ${updated[existingIndex].quantity} ${productData?.Unit || ""})`,
              "success",
              "productScan",
            );
            return updated;
          } else {
            // Add new item
            const newItem: ScannedItem = {
              id: `${Date.now()}-${Math.random()}`,
              productCode: parsed.productCode,
              productName: productData?.ProductName,
              batchNumber: parsed.batchNumber,
              quantity: parsed.quantity,
              unit: productData?.Unit,
              skuId: productData?.SkuId,
            };
            showFeedbackWithSound(
              `Pridané: ${formatBarcodeForDisplay(parsed, productData?.Unit)}`,
              "success",
              "productScan",
            );
            return [...prev, newItem];
          }
        });
        setScanPhase("SCANNING_ITEMS");
      } else {
        showFeedbackWithSound(
          "Najprv naskenujte zdrojovú pozíciu!",
          "error",
          "noPosition",
        );
      }
    },
    [
      scanPhase,
      fromPosition,
      scannedItems,
      showFeedbackWithSound,
      loadProductData,
      moveMode,
      loadPositionInventory,
      addDebugLog,
      sourcePositionInventory,
    ],
  );

  // Camera barcode scanner
  const handleCameraScan = React.useCallback(
    async (result: BarcodeScanResult) => {
      await processScannedCode(result.rawValue);
    },
    [processScannedCode],
  );

  // Keyboard scanner (HID scanner)
  useKeyboardScanner(processScannedCode);

  // Remove item from list
  const removeItem = (itemId: string) => {
    setScannedItems((prev) => prev.filter((item) => item.id !== itemId));
    if (scannedItems.length <= 1) {
      setScanPhase("SCANNING_ITEMS");
      setToPosition("");
    }
  };

  // Submit the move request
  const handleSubmit = async () => {
    if (scannedItems.length === 0) {
      antdMessage.error("Pridajte aspoň jeden produkt!");
      return;
    }

    if (!fromPosition || !toPosition) {
      antdMessage.error("Zadajte zdrojovú aj cieľovú pozíciu!");
      return;
    }

    // Validate all items have a valid skuId
    const itemsWithoutSkuId = scannedItems.filter((item) => !item.skuId);
    if (itemsWithoutSkuId.length > 0) {
      const productCodes = itemsWithoutSkuId
        .map((i) => i.productCode)
        .join(", ");
      antdMessage.error(
        `Chýbajúce ID produktov: ${productCodes}. Odstráňte položky a znova ich naskenujte.`,
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const moveItems: MoveItemDto[] = scannedItems.map((item) => ({
        SkuId: item.skuId!,
        SkuCode: item.productCode,
        BatchNumber: item.batchNumber,
        Quantity: item.quantity,
        BatchExpirationDate: null,
      }));

      await inventoryClient.move({
        FromStoragePositionId: fromPosition,
        ToStoragePositionId: toPosition,
        AccountingDocumentNumber: null,
        Items: moveItems,
      });

      // Capture current positions for use in modal callbacks
      const sourcePosition = fromPosition;
      const targetPosition = toPosition;

      // Show success modal with option to continue or reset
      Modal.confirm({
        title: "Presun úspešný!",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        content: `Presun z ${sourcePosition} na ${targetPosition} bol úspešne dokončený. Chcete vykonať ďalší presun z rovnakej pozície?`,
        okText: "Áno, pokračovať",
        cancelText: "Nie, ukončiť",
        onOk: () => {
          // Keep source position for next move, clear destination and items
          setScannedItems([]);
          setToPosition("");
          setScanPhase("SCANNING_ITEMS");
          // Reload position inventory to reflect the move
          loadPositionInventory(sourcePosition);
          showNotification(
            `Zdrojová pozícia ${sourcePosition} zachovaná. Skenujte ďalšie produkty.`,
            "success",
          );
        },
        onCancel: () => {
          // Full reset
          setScannedItems([]);
          setFromPosition("");
          setToPosition("");
          setScanPhase("WAITING_SOURCE");
          setSourcePositionInventory([]);
          clearLocalStorage();
          showNotification(
            "Naskenujte zdrojovú pozíciu pre nový presun.",
            "info",
          );
        },
      });
    } catch (error) {
      console.error("Move failed:", error);
      const errorMsg = getApiErrorMessage(error);
      antdMessage.error(`Chyba pri presune: ${errorMsg}`);
      showNotification(`Chyba: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset everything
  const handleReset = () => {
    Modal.confirm({
      title: "Zrušiť presun?",
      content: "Všetky naskenované položky budú odstránené.",
      okText: "Áno, zrušiť",
      cancelText: "Nie",
      onOk: () => {
        setScannedItems([]);
        setFromPosition("");
        setToPosition("");
        setScanPhase("WAITING_SOURCE");
        setSourcePositionInventory([]);
        setIsLoadingInventory(false);
        clearLocalStorage();
        notify.destroy(); // Clear any active notifications
        showNotification(
          "Presun zrušený. Naskenujte zdrojovú pozíciu.",
          "info",
        );
      },
    });
  };

  // Set source position to user's position (button click, not scan - no sound)
  const handleUseMySourcePosition = () => {
    if (!userPosition) return;
    setFromPosition(userPosition);
    setScanPhase("SCANNING_ITEMS");
    showNotification(
      `Zdrojová pozícia: ${userPosition}. Skenujte produkty.`,
      "success",
    );
    loadPositionInventory(userPosition);
  };

  // Set target position to user's position (button click, not scan - no sound)
  const handleUseMyTargetPosition = () => {
    if (!userPosition) return;
    if (userPosition === fromPosition) {
      showNotification(
        "Cieľová pozícia nemôže byť rovnaká ako zdrojová!",
        "error",
      );
      return;
    }
    setToPosition(userPosition);
    setScanPhase("READY_TO_SUBMIT");
    showNotification(
      `Cieľová pozícia: ${userPosition}. Skontrolujte a potvrďte presun.`,
      "success",
    );
  };

  // Create temporary position and print label
  const handleCreateTemporaryPosition = async () => {
    setIsCreatingTempPosition(true);
    try {
      // Create temporary position
      const response = await inventoryClient.createTemporaryPosition();
      const positionId = response.PositionId;

      // Print label (button click, not scan - no sound)
      try {
        await positionLabelClient.printTemporary(
          positionId,
          selectedPrinter,
          null,
        );
        setToPosition(positionId);
        setScanPhase("READY_TO_SUBMIT");
        showNotification(
          `Dočasná pozícia ${positionId} vytvorená a štítok vytlačený!`,
          "success",
        );
      } catch (printErr) {
        console.error("Failed to print label:", printErr);
        // Still set the position even if printing failed
        setToPosition(positionId);
        setScanPhase("READY_TO_SUBMIT");
        showNotification(
          `Pozícia ${positionId} vytvorená, ale tlač zlyhala: ${getApiErrorMessage(printErr)}`,
          "warning",
        );
      }
    } catch (error) {
      console.error("Failed to create temporary position:", error);
      showNotification(
        `Chyba pri vytváraní pozície: ${getApiErrorMessage(error)}`,
        "error",
      );
    } finally {
      setIsCreatingTempPosition(false);
    }
  };

  // Update item quantity manually
  const updateQuantity = React.useCallback(
    (itemId: string, newQuantity: number) => {
      setScannedItems((prev) => {
        const itemToUpdate = prev.find((i) => i.id === itemId);
        if (!itemToUpdate) return prev;

        // Validate against available stock
        if (fromPosition && itemToUpdate.skuId) {
          const productData = productLocations.get(itemToUpdate.skuId);
          const stockEntries = productData?.StockEntries || [];
          const stockAtSource = stockEntries.find(
            (entry) =>
              entry.PositionId === fromPosition &&
              entry.BatchNumber === itemToUpdate.batchNumber,
          );

          if (stockAtSource && stockAtSource.Quantity !== null) {
            // Calculate total quantity already allocated (excluding current item)
            const otherItemsQuantity = prev
              .filter(
                (item) =>
                  item.id !== itemId &&
                  item.skuId === itemToUpdate.skuId &&
                  item.batchNumber === itemToUpdate.batchNumber,
              )
              .reduce((sum, item) => sum + item.quantity, 0);

            const availableQuantity = stockAtSource.Quantity;
            const maxAllowed = availableQuantity - otherItemsQuantity;

            if (newQuantity > maxAllowed) {
              showNotification(
                `Nedostatočné množstvo! Maximálne: ${maxAllowed} ${itemToUpdate.unit}`,
                "error",
              );
              return prev; // Don't update
            }
          }
        }

        return prev.map((item) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item,
        );
      });
    },
    [fromPosition, productLocations, showNotification],
  );

  // Check if any item has a batch number
  const hasBatchNumbers = scannedItems.some((item) => item.batchNumber);

  // Table columns
  const columns = [
    {
      title: "Produkt",
      key: "product",
      render: (item: ScannedItem) => (
        <div>
          <strong>{item.productCode}</strong>
          {item.productName && (
            <>
              <br />
              <span style={{ color: "#666" }}>{item.productName}</span>
            </>
          )}
        </div>
      ),
    },
    ...(hasBatchNumbers
      ? [
          {
            title: "Šarža",
            dataIndex: "batchNumber",
            key: "batchNumber",
            render: (text: string) => text || "-",
          },
        ]
      : []),
    {
      title: "Množstvo",
      key: "quantity",
      render: (item: ScannedItem) => (
        <NumericKeyboard
          title="Zadajte množstvo"
          unit={item.unit}
          value={item.quantity}
          onChange={(value) => updateQuantity(item.id, value)}
          onEnter={() => {
            // Focus next action or just close the popover
          }}
        >
          <Button
            style={{ width: "100%", minWidth: "90px", textAlign: "left" }}
            disabled={isSubmitting}
          >
            {item.quantity} {item.unit}
          </Button>
        </NumericKeyboard>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (item: ScannedItem) => (
        <Button
          danger
          shape="circle"
          icon={<DeleteOutlined />}
          onClick={() => removeItem(item.id)}
          disabled={isSubmitting}
        />
      ),
    },
  ];

  // Expanded row render for product locations
  const expandedRowRender = (item: ScannedItem) => {
    if (!item.skuId) return null;

    const productData = productLocations.get(item.skuId);
    const locations = productData?.StockEntries || [];
    const hasLocations = locations.length > 0;

    // Check if any location has a batch number to conditionally show the column
    const locationHasBatchNumbers = locations.some((loc) => loc.BatchNumber);

    const locationColumns = [
      {
        title: "Pozícia",
        dataIndex: "PositionId",
        key: "positionId",
        render: (pos: string, record: ProductStockEntryDto) => (
          <span>
            {pos} {record.IsTemporary && <Tag color="orange">Dočasná</Tag>}
          </span>
        ),
      },
      ...(locationHasBatchNumbers
        ? [
            {
              title: "Šarža",
              dataIndex: "BatchNumber",
              key: "batchNumber",
              render: (text: string) => text || "-",
            },
          ]
        : []),
      {
        title: "Množstvo",
        dataIndex: "Quantity",
        key: "quantity",
        render: (qty: number | null) =>
          qty !== null ? `${qty} ${item.unit || ""}` : "-",
      },
    ];

    return (
      <div style={{ padding: "8px 0" }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          <InfoCircleOutlined /> Produkt sa už nachádza na pozíciách (
          {locations.length})
        </div>
        {hasLocations ? (
          <Table
            dataSource={locations}
            columns={locationColumns}
            rowKey={(record) => `${record.PositionId}-${record.BatchNumber}`}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Produkt sa nenachádza na žiadnej pozícii"
          />
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "0 0 80px 0",
        paddingBottom: cameraStatus !== "idle" ? "calc(25vh + 140px)" : "140px",
      }}
    >
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Presun tovaru"
      />

      {/* Mode Selection */}
      <div
        style={{
          marginBottom: 16,
          padding: 16,
          background: "#fafafa",
          borderRadius: 4,
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <label style={{ marginRight: 16, fontWeight: 500 }}>
              Typ cieľovej pozície:
            </label>
            <Radio.Group
              value={moveMode}
              onChange={(e) => setMoveMode(e.target.value)}
              disabled={scanPhase !== "WAITING_SOURCE"}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="permanent">Trvalá</Radio.Button>
              <Radio.Button value="temporary">Dočasná</Radio.Button>
            </Radio.Group>
          </div>
          {moveMode === "temporary" && (
            <>
              <div>
                <label style={{ marginRight: 16, fontWeight: 500 }}>
                  Tlačiareň:
                </label>
                <Radio.Group
                  value={selectedPrinter}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value={6101}>SKLAD (6101)</Radio.Button>
                  <Radio.Button value={6102}>HALA (6102)</Radio.Button>
                </Radio.Group>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={handleCreateTemporaryPosition}
                loading={isCreatingTempPosition}
                block
                style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
              >
                <PrinterOutlined style={{ marginLeft: 8 }} />
                {isCreatingTempPosition
                  ? "Vytváram pozíciu..."
                  : "Vytvoriť dočasnú pozíciu a vytlačiť štítok"}
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Instructions */}
      <Alert
        message="Ako používať nástroj"
        description={
          <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>
              Naskenujte <strong>zdrojovú pozíciu</strong>
            </li>
            <li>
              Naskenujte <strong>produkty</strong> ktoré chcete presunúť
            </li>
            {moveMode === "permanent" ? (
              <li>
                Naskenujte <strong>cieľovú pozíciu</strong>
              </li>
            ) : (
              <li>
                Vytvorte <strong>dočasnú pozíciu</strong> tlačidlom nižšie
              </li>
            )}
            <li>
              Skontrolujte zoznam a kliknite na <strong>Potvrdiť presun</strong>
            </li>
          </ol>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Status */}
      <Descriptions
        column={{ xs: 1, sm: 2 }}
        bordered
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Krok">
          {scanPhase === "WAITING_SOURCE" && "1. Čakám na zdrojovú pozíciu"}
          {scanPhase === "SCANNING_ITEMS" && "2. Skenujem produkty"}
          {scanPhase === "READY_TO_SUBMIT" && "3. Pripravené na odoslanie"}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {isSubmitting ? (
            <Tag color="blue">Odosielam...</Tag>
          ) : scanPhase === "READY_TO_SUBMIT" ? (
            <Tag color="green">Pripravené</Tag>
          ) : (
            <Tag color="orange">Skenovanie</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <>
              <EnvironmentOutlined /> Z pozície
            </>
          }
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {fromPosition ? (
              <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                {fromPosition}
              </Tag>
            ) : (
              <span style={{ color: "#999" }}>Naskenujte pozíciu</span>
            )}
            {isUserPositionAvailable && scanPhase === "WAITING_SOURCE" && (
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={handleUseMySourcePosition}
                type="primary"
              >
                Nastaviť {userPosition}
              </Button>
            )}
          </div>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <>
              <EnvironmentOutlined /> Na pozíciu
            </>
          }
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {toPosition ? (
              <Tag color="green" style={{ fontSize: 16, padding: "4px 12px" }}>
                {toPosition}
              </Tag>
            ) : (
              <span style={{ color: "#999" }}>Naskenujte cieľovú pozíciu</span>
            )}
            {isUserPositionAvailable &&
              moveMode === "permanent" &&
              (scanPhase === "SCANNING_ITEMS" ||
                scanPhase === "READY_TO_SUBMIT") &&
              scannedItems.length > 0 && (
                <Button
                  size="small"
                  icon={<EnvironmentOutlined />}
                  onClick={handleUseMyTargetPosition}
                  type="primary"
                >
                  Nastaviť {userPosition}
                </Button>
              )}
          </div>
        </Descriptions.Item>
        <Descriptions.Item label="Naskenované položky">
          {scannedItems.length}
        </Descriptions.Item>
      </Descriptions>

      {/* Source Position Inventory - Loading State */}
      {fromPosition &&
        isLoadingInventory &&
        (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") && (
          <Alert
            message="Načítavam produkty z pozície..."
            description={`Kontrolujem obsah pozície ${fromPosition}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

      {/* Source Position Inventory - Empty State */}
      {fromPosition &&
        !isLoadingInventory &&
        sourcePositionInventory?.length === 0 &&
        (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") && (
          <Alert
            message="Prázdna pozícia"
            description={`Na pozícii ${fromPosition} sa nenachádzajú žiadne produkty.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

      {/* Source Position Inventory Overview */}
      {fromPosition &&
        sourcePositionInventory?.length > 0 &&
        (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") &&
        (() => {
          // Calculate moved and remaining quantities for each product in the source position
          const inventoryWithStatus = sourcePositionInventory
            .map((item) => {
              const movedQuantity = scannedItems
                .filter(
                  (scanned) =>
                    scanned.skuId === item.SkuId &&
                    scanned.batchNumber === item.BatchNumber,
                )
                .reduce((sum, scanned) => sum + scanned.quantity, 0);

              const remainingQuantity =
                item.Quantity !== null ? item.Quantity - movedQuantity : null;

              return {
                productCode: item.ProductCode,
                productName: item.ProductName,
                batchNumber: item.BatchNumber,
                totalAtSource: item.Quantity,
                unit: item.Unit,
                skuId: item.SkuId,
                suggestedPosition: item.SuggestedPosition,
                movedQuantity,
                remainingQuantity,
              };
            })
            .sort((a, b) => {
              // Sort by suggested position first (nulls last), then by product code
              if (!a.suggestedPosition && !b.suggestedPosition)
                return a.productCode.localeCompare(b.productCode);
              if (!a.suggestedPosition) return 1;
              if (!b.suggestedPosition) return -1;
              const posCompare = a.suggestedPosition.localeCompare(
                b.suggestedPosition,
              );
              if (posCompare !== 0) return posCompare;
              return a.productCode.localeCompare(b.productCode);
            });

          // Check if any item has a batch number
          const hasBatchNumbersInOverview = inventoryWithStatus.some(
            (item) => item.batchNumber,
          );

          const overviewColumns = [
            {
              title: "Produkt",
              key: "product",
              render: (item: any) => (
                <div>
                  <strong>{item.productCode}</strong>
                  {item.productName && (
                    <>
                      <br />
                      <span style={{ color: "#666", fontSize: "0.9em" }}>
                        {item.productName}
                      </span>
                    </>
                  )}
                </div>
              ),
            },
            ...(hasBatchNumbersInOverview
              ? [
                  {
                    title: "Šarža",
                    dataIndex: "batchNumber",
                    key: "batchNumber",
                    render: (text: string) => text || "-",
                  },
                ]
              : []),
            {
              title: "Celkom",
              key: "total",
              align: "right" as const,
              render: (item: any) => (
                <span style={{ fontWeight: 500 }}>
                  {item.totalAtSource !== null
                    ? `${item.totalAtSource} ${item.unit || ""}`
                    : "-"}
                </span>
              ),
            },
            {
              title: "Presunuté",
              key: "moved",
              align: "right" as const,
              render: (item: any) => (
                <span
                  style={{ color: item.movedQuantity > 0 ? "#1890ff" : "#999" }}
                >
                  {item.movedQuantity} {item.unit || ""}
                </span>
              ),
            },
            {
              title: "Zostáva",
              key: "remaining",
              align: "right" as const,
              render: (item: any) => (
                <span
                  style={{
                    fontWeight: 500,
                    color:
                      item.remainingQuantity === null
                        ? "#999"
                        : item.remainingQuantity === 0
                          ? "#52c41a"
                          : item.remainingQuantity < 0
                            ? "#ff4d4f"
                            : "#000",
                  }}
                >
                  {item.remainingQuantity !== null
                    ? `${item.remainingQuantity} ${item.unit || ""}`
                    : "-"}
                </span>
              ),
            },
            {
              title: "Navrhovaná pozícia",
              key: "suggested",
              align: "center" as const,
              render: (item: any) =>
                item.suggestedPosition ? (
                  <Tag
                    color="green"
                    style={{ fontSize: "13px", fontWeight: 500 }}
                  >
                    <EnvironmentOutlined /> {item.suggestedPosition}
                  </Tag>
                ) : (
                  <span style={{ color: "#999" }}>-</span>
                ),
            },
          ];

          return (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  background: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  borderRadius: 4,
                  padding: 12,
                  marginBottom: 8,
                }}
              >
                <InfoCircleOutlined
                  style={{ color: "#1890ff", marginRight: 8 }}
                />
                <strong>Prehľad zdrojovej pozície {fromPosition}</strong>
              </div>
              <Table
                dataSource={inventoryWithStatus}
                columns={overviewColumns}
                rowKey={(item) => `${item.skuId}-${item.batchNumber}`}
                pagination={false}
                size="small"
                scroll={{ x: true }}
              />
            </div>
          );
        })()}

      {/* Scanned Items Table */}
      <Table
        dataSource={scannedItems}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: true }}
        expandable={{
          expandedRowRender,
          defaultExpandAllRows: true,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Žiadne naskenované položky"
            />
          ),
        }}
      />

      {/* Action Buttons - Fixed at bottom */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: "16px",
          zIndex: 100,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "600px" }}>
          <style>{`
            .inventory-move-button-row .ant-space-item:last-child {
              flex-grow: 1;
            }
          `}</style>

          <Space
            className="inventory-move-button-row"
            style={{ width: "100%", display: "flex" }}
          >
            {/* Camera Scanner - always mounted */}
            <CameraScanner
              onScan={handleCameraScan}
              onCameraStatusChange={setCameraStatus}
              buttonSize="large"
            />

            {/* Reset and Submit buttons - hidden when camera is active */}
            {cameraStatus === "idle" && (
              <>
                {/* Reset Button */}
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={isSubmitting}
                  style={{ height: 48 }}
                />

                {/* Submit Button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={
                    scanPhase !== "READY_TO_SUBMIT" || scannedItems.length === 0
                  }
                  block
                  style={{ height: 48 }}
                >
                  {isSubmitting
                    ? "Odosielam..."
                    : `Presunúť z ${fromPosition} na ${toPosition}`}
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>

      {/* DEBUG: Toggle button */}
      <button
        onClick={() => setShowDebugLog((prev) => !prev)}
        style={{
          position: "fixed",
          bottom: 80,
          right: 8,
          background: showDebugLog ? "#333" : "#666",
          color: "#fff",
          border: "none",
          padding: "4px 8px",
          borderRadius: 4,
          fontSize: 10,
          cursor: "pointer",
          zIndex: 100,
          opacity: 0.7,
        }}
      >
        Debug
      </button>

      {/* DEBUG: Visible log panel */}
      {showDebugLog && (
        <div
          style={{
            position: "fixed",
            bottom: 80,
            left: 8,
            right: 8,
            background: "rgba(0,0,0,0.85)",
            color: "#0f0",
            fontFamily: "monospace",
            fontSize: 11,
            padding: 8,
            borderRadius: 4,
            maxHeight: 150,
            overflow: "auto",
            zIndex: 99,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <strong>Debug Log (@ = charCode 64)</strong>
            <button
              onClick={() => setDebugLog([])}
              style={{
                background: "#333",
                color: "#fff",
                border: "none",
                padding: "2px 8px",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
          {debugLog.length > 0 ? (
            debugLog.map((log, i) => <div key={i}>{log}</div>)
          ) : (
            <div style={{ color: "#666" }}>No logs yet</div>
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryMove;
